/*
Handles the tracking basics and allows to send events with free parameters to the server.
Sets a not personalized session id as well as a not personalized user id.
*/
import liteguid from "lite-guid";
import { sha256 } from "sha.js";
import TrackingState from "../stores/TrackingState";
import { ClientSession } from "../utils/ClientSessionHolder";
import CountryUtil from "../utils/CountryUtil";
import SessionState from "../stores/SessionState";
import TrackingStore from "../stores/TrackingStore";
export type TrackingSessionData = {
	sessionId: string;
	timestamp: number;
};
export type TrackingEvent = {
	vertical: string;
	service: string;
	eventType: string;
	eventId: string;
	structuredData: Record<string, any>;
	dispatcherData: Record<string, any>;
};
const hash = {
	cache: Object,
	calculate: function (val: string): any {
		if (!val) {
			return "";
		}

		const valKey = "_" + val; // we do not want to overwrite any property of the cache object

		if (!Object.hasOwn(this.cache, valKey)) {
			const hashValue = new sha256().update(val, "utf8").digest("hex");
			this.cache[valKey] = hashValue;
		}

		return this.cache[valKey];
	},
};

function getLang(): any {
	return window.lang;
}

/*
 * Copyright 2011-2013 Adobe Systems, Inc.
 * s_getLoadTime v1.36 - Get page load time in units of 1/10 seconds
 */
function getLoadTime(): any {
	//@ts-expect-error: s_loadT not defined in global
	if (!window.s_loadT) {
		const b = new Date().getTime();
		const o = window.performance ? performance.timing : 0;
		//@ts-expect-error: inHeadTS not defined in global
		const a = o ? o.requestStart : window.inHeadTS || 0;
		return a ? Math.round((b - a) / 100) : "";
	}
	//@ts-expect-error: s_loadT not defined in global
	return window.s_loadT;
}

function sanitizeSearchString(string: string): string {
	return (string || "").toLowerCase().replace(/\s\s+/g, " ").trim();
}

function getPageName(state: Record<string, any>): string {
	let pageName;

	if (state.routeParams && state.routeParams.trackingName) {
		pageName = state.routeParams.trackingName;
	} else {
		pageName = state.path.replace("/", "");
		console.info("No t-name set for ", state.path);
	}

	return pageName;
}

function getPageForFooter(): string {
	const currentPath = window && window.location && window.location.pathname;
	return currentPath.includes("checkout") ? "checkout" : "other";
}

function getURLWithoutQuery(path: string): string {
	const end = path.indexOf("?");
	return end < 0 ? path : path.slice(0, end);
}

function getHref(): string {
	return window.location.href;
}

function buildUrl(url: string, parameters: any): string {
	let qs = "";

	if (
		Object.keys(parameters).length > 30 &&
		parameters.eventType !== "UI_ERROR" &&
		parameters.eventType !== "javascriptperformance"
	) {
		console.error("Too many parameters for tracking call for eventType " + parameters.eventType);
	}

	for (const key in parameters) {
		const value = parameters[key];

		if (Array.isArray(value)) {
			for (const arrayEntry of value) {
				qs += encodeURIComponent(key) + "=" + encodeURIComponent(arrayEntry) + "&";
			}
		} else {
			qs += encodeURIComponent(key) + "=" + encodeURIComponent(value) + "&";
		}
	}

	if (qs.length > 0) {
		qs = qs.substring(0, qs.length - 1); //chop off last "&"

		url = url + "?" + qs;
	}

	return url;
}

function parseTagObject(tags: any): any {
	return Object.entries(tags ?? {})
		.filter(([_, value]) => value !== undefined && value !== null)
		.map(([key, value]) => `${key}:${value}`);
}

function generateStructuredEventUrl(trackingEvent: TrackingEvent, sessionData: TrackingSessionData): string {
	const { sessionId, timestamp } = sessionData;
	const { eventGroupId, eventInfo, metric, tags } = trackingEvent.structuredData ?? {};
	const {
		clientSessionId,
		anonymousUserId,
		customerId,
		userId,
		employeeId,
		userType,
		technicalUserId,
		country,
		store,
		loggedIn,
		salesLine,
		callTreeId,
	} = trackingEvent.dispatcherData.sessionStoreInfo;
	const { contextId, contextType, subContextId, subContextType } = trackingEvent.dispatcherData.contextInfo;
	const { userAgent, viewportWidth, viewportHeight, language, clientTimestamp } =
		trackingEvent.dispatcherData.userInfo;
	const tagArray = parseTagObject(tags);
	const testGroupArray = parseTagObject(trackingEvent.dispatcherData.testGroups);
	const requiredParams = {
		vertical: trackingEvent.vertical,
		service: trackingEvent.service,
		eventId: trackingEvent.eventId,
		callTreeId: callTreeId,
		customerId: customerId,
		userId: userId,
		employeeId: employeeId,
		technicalUserId: technicalUserId,
		clientSessionId: clientSessionId,
		sessionId: sessionId,
		sessionTimestamp_v1: timestamp,
		country: country,
		store: store,
		loggedIn: loggedIn,
		salesLine: salesLine,
		eventGroupId: eventGroupId,
		metric: metric,
		eventInfo: eventInfo,
		tag: tagArray,
		anonymousUserId: anonymousUserId,
		contextId: contextId,
		contextType: contextType,
		subContextId: subContextId,
		subContextType: subContextType,
		userAgent: userAgent,
		testGroup: testGroupArray,
		userType: userType,
		viewportHeight: viewportHeight,
		viewportWidth: viewportWidth,
		language: language,
		clientTimestamp: clientTimestamp,
		_: Math.random(), // Cache piercing
	};
	const allParams = Object.fromEntries(Object.entries(requiredParams).filter(([, value]) => value !== undefined));
	return buildUrl("/explore.tracking.v1/tracking/track-structured/eventType/" + trackingEvent.eventType, allParams);
}

function getUserType(
	customerId: string | null | undefined,
	employeeId: string | null | undefined,
	anonymousUserId: string | null | undefined,
	technicalUserId: string | null | undefined,
): string | null | undefined {
	if (technicalUserId) {
		return "technical";
	}

	if (employeeId) {
		return "employee";
	}

	if (customerId) {
		return "customer";
	}

	if (anonymousUserId) {
		return "anonymous";
	}

	return undefined;
}

type UserInfo = {
	userAgent: string;
	viewportHeight: number;
	viewportWidth: number;
	language: any;
	clientTimestamp: string;
};

function getUserInfo(): UserInfo {
	const userAgent = navigator ? navigator.userAgent : "";
	const element = document.documentElement;
	const viewportWidth = Math.max(element ? element.clientWidth : 0, window.innerWidth || 0);
	const viewportHeight = Math.max(element ? element.clientHeight : 0, window.innerHeight || 0);
	const language = getLang();
	const clientTimestamp = Date.now().toString() || "";
	return {
		userAgent,
		viewportHeight,
		viewportWidth,
		language,
		clientTimestamp,
	};
}

type ContextInfo = {
	contextId: string;
	contextType: string;
	subContextId: string;
	subContextType: string;
};

function getContextInfo(): ContextInfo {
	return {
		contextId: TrackingState.getContextId() || "",
		contextType: TrackingState.getContextType() || "",
		subContextId: TrackingState.getSubContextId() || "",
		subContextType: TrackingState.getSubContextType() || "",
	};
}

function getCallTreeId(existingId?: string): string {
	if (existingId) {
		return existingId;
	}

	return ClientSession ? ClientSession.generateSessionedCallTreeIdWithPrefix("TRACK") : "TRACK-" + liteguid.create();
}

type SessionStoreInfo = {
	storeLoaded: boolean;
	customerId: string;
	userId: string | null | undefined;
	anonymousUserId: string;
	clientSessionId: string | null | undefined;
	employeeId: string;
	userType: string | null | undefined;
	technicalUserId: string | null | undefined;
	country: string;
	store: string;
	loggedIn: string | null | undefined;
	salesLine: string | null | undefined;
	callTreeId: string;
	sessionId: string;
};

function getSessionStoreInfo(callTreeId?: string): SessionStoreInfo {
	try {
		const state = SessionState.getState();
		const hashedCustomerId = hash.calculate(state.customerId || "");
		const anonymousUserId = state.anonymousUserId || "";
		const clientSessionId = ClientSession.getClientSessionId() || "";
		const employeeId = state.employeeData ? state.employeeData.upn : null;
		const hashedEmployeeId = hash.calculate(employeeId || "");
		const technicalUserId = undefined;
		return {
			storeLoaded: !SessionState.pending(),
			customerId: hashedCustomerId,
			userId: hashedCustomerId,
			// "GA360_userId" provides the hashed customer id to DBU/business as part of CIA-1005. "customerId" also currently provides the hashed customer ID, but was requested by another team, and might change in the future.
			anonymousUserId: anonymousUserId,
			clientSessionId: clientSessionId,
			employeeId: hashedEmployeeId,
			userType: getUserType(hashedCustomerId, hashedEmployeeId, anonymousUserId, technicalUserId),
			technicalUserId: technicalUserId,
			country: CountryUtil.getCountry() || "",
			store: SessionState.getCustomerStore() || "",
			loggedIn: SessionState.loggedIn() ? "logged-in" : "not-logged-in",
			salesLine: state.salesLine,
			callTreeId: getCallTreeId(callTreeId),
			sessionId: "",
		};
	} catch (e) {
		console.warn(e);
		return {
			storeLoaded: false,
			customerId: "",
			userId: "",
			anonymousUserId: "",
			clientSessionId: undefined,
			employeeId: "",
			userType: undefined,
			technicalUserId: undefined,
			country: CountryUtil.getCountry(),
			store: "",
			loggedIn: undefined,
			salesLine: undefined,
			callTreeId: "unknown",
			sessionId: "",
		};
	}
}

function getTestGroups(): any {
	return TrackingState.getTestGroups();
}

function getExtraTrackingData(): Record<string, any> {
	const userAgent = navigator ? navigator.userAgent : "";
	const { country } = getSessionStoreInfo();
	return {
		userAgent,
		country,
	};
}

function extractHostname(url: string): string {
	let hostname;

	//find & remove protocol (http, ftp, etc.) and get hostname
	if (url.indexOf("//") > -1) {
		hostname = url.split("/")[2];
	} else {
		hostname = url.split("/")[0];
	}

	//find & remove port number
	hostname = hostname.split(":")[0];
	//find & remove "?"
	hostname = hostname.split("?")[0];
	return hostname;
}

function getBannerTargetLinkWithinMShop(
	targetLink: string | null | undefined,
	linkType: string | null | undefined,
): boolean {
	if (!targetLink) {
		return false;
	}

	if (linkType !== "PURE_LINK") {
		// must be some kind of relative link hence within MShop
		return true;
	}

	const targetHost = extractHostname(targetLink);
	return targetHost === window.location.hostname;
}

function getCustomerInformation(): Record<string, any> {
	const customerInformation = TrackingStore.getCustomerInformation(SessionState.getState().customerId) || {};
	return { ...customerInformation, customerTierLevel: SessionState.getState()?.customerTierLevel };
}

type PersonInformation = {
	hashedPersonId: string;
};

function getPersonInformation(): PersonInformation | null | undefined {
	try {
		if (!SessionState.loggedIn()) {
			return undefined;
		}

		const personId = TrackingStore.getPersonId();
		const hashedPersonId = hash.calculate(personId || "");
		return hashedPersonId
			? {
					hashedPersonId: hashedPersonId,
				}
			: undefined;
	} catch {
		return undefined;
	}
}

type ActiveContext = {
	hashedActiveContext: string;
};

function getActiveContext(): ActiveContext | null | undefined {
	try {
		if (!SessionState.loggedIn()) {
			return undefined;
		}

		const activeContext = TrackingStore.getActiveContext();
		const hashedActiveContext = hash.calculate(activeContext || "");
		return hashedActiveContext
			? {
					hashedActiveContext: hashedActiveContext,
				}
			: undefined;
	} catch {
		return undefined;
	}
}

export default {
	extractHostname,
	getLang,
	sanitizeSearchString,
	getPageName,
	getPageForFooter,
	generateStructuredEventUrl,
	getExtraTrackingData,
	getSessionStoreInfo,
	getContextInfo,
	getUserInfo,
	getUserType,
	getURLWithoutQuery,
	getHref,
	getLoadTime,
	getTestGroups: getTestGroups,
	getBannerTargetLinkWithinMShop,
	getCustomerInformation,
	getPersonInformation,
	getActiveContext,
	hash,
};
